import Images from '../assets/images';
import { Line, Column } from "@ant-design/plots";
import { DatePicker, Segmented, Select, Space, } from 'antd';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import { useFetchLastWeekOverviewChartDetail, useFetchLastWeekProfitChartDetail, useFetchProfitDetail, useFetchTopProfitableStockSymbol, useFetchTopProfitableTrades, useFetchTopStopLossTrades } from '../services/dashboard.service';
import { formatDateTime, formatNumberWithCommas } from '../helpers/common';
import { LastWeekOverviewChartData, LastWeekProfitChartData, TopStockSymbol, TopTrades } from '../types/dashboard';
import { IResponse, IResponseModel } from '../types/response';
import { UserAccounts } from '../types/userAccount';
import { useFetchUserAccount } from '../services/useraccount.service';

const Dashboard = () => {
    const profitableFilterValue = React.useRef<Align>('Last 1 month');
    const stopLossFilterValue = React.useRef<Align>('Last 1 month');
    type Align = 'Today' | 'Yesterday' | 'Last 7 day' | 'Last 1 month';
    const totalProfit = useRef<string>("0.00");
    const totalLoss = useRef<string>("0.00");
    const todayProfit = useRef<string>("0.00");
    const todayLoss = useRef<string>("0.00");
    const activeTrades = useRef<number>(0);
    const lastWeekTradesData = useRef<LastWeekProfitChartData[]>([]);
    const lastWeekTradeClosedTotalPrice = useRef<string>("0.00");
    const lastWeekTotalTrades = useRef<number>(0);
    const lastWeekTotalProfit = useRef<string>("0.00");
    const lastWeekTotalLoss = useRef<string>("0.00");
    const lastWeekOverviewChartData = useRef<LastWeekOverviewChartData[]>([]);
    const [userAccount, setUserAccountList] = useState<any[]>([]);
    const [userAccountId, setUserAccountId] = useState<any>("");
    const topProfitableTrades = useRef<TopTrades[]>([]);
    const topStopLossTrades = useRef<TopTrades[]>([]);
    const topProfitableStock = useRef<TopStockSymbol[]>([]);
    const [dateRange, setDateRange] = useState(["", ""]);
    const { RangePicker } = DatePicker;

    useEffect(() => {
        getUserAccount.mutate({});
        getProfitDetail.mutate({ userAccountId: "" });
        getLastWeekProfitChartDetail.mutate({ userAccountId: "" });
        getLastWeekOverviewChartDetail.mutate({ userAccountId: "" });
        fetchTopProfitableData('Last 1 month');
        fetchTopStopLossData('Last 1 month');
        fetchTopProfitableStockSymbolData();
    }, []);


    const getUserAccount = useFetchUserAccount({
        onSuccess: (response: IResponseModel<UserAccounts>) => {
            if (response.statusCode === 200) {
                const options = response.data.map((account: { userAccountName: any; userAccountId: any; }) => ({
                    label: account.userAccountName,
                    value: account.userAccountId,
                }));
                setUserAccountList(options);
            }
            else {
                setUserAccountList([]);
            }
        }
    });

    const handleUserAccountChange = (value: any) => {
        const userAccountId = value ?? "";
        setUserAccountId(userAccountId);
        onClearRefresh(userAccountId);
    };

    const onClearRefresh = (userAccountId: any) => {
        getProfitDetail.mutate({ userAccountId: userAccountId });
        getLastWeekProfitChartDetail.mutate({ userAccountId: userAccountId });
        getLastWeekOverviewChartDetail.mutate({ userAccountId: userAccountId });
        getTopProfitableTrades.mutate({ userAccountId: userAccountId, filterRangeValue: 'Last 1 month' });
        getTopStopLossTrades.mutate({ userAccountId: userAccountId, filterRangeValue: 'Last 1 month' });
        getTopProfitableStockSymbol.mutate({ userAccountId: userAccountId, startDate: dateRange[0], endDate: dateRange[1] });
    };

    const getProfitDetail = useFetchProfitDetail({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                totalProfit.current = formatNumberWithCommas(response.data.totalProfit);
                totalLoss.current = formatNumberWithCommas(response.data.totalLoss);
                todayProfit.current = formatNumberWithCommas(response.data.todayProfit);
                todayLoss.current = formatNumberWithCommas(response.data.todayLoss);
                activeTrades.current = parseInt(response.data.activeTrades);
            } else {
                totalProfit.current = "0.00";
                totalLoss.current = "0.00";
                todayProfit.current = "0.00";
                todayLoss.current = "0.00";
                activeTrades.current = 0;
            }
        },
    });

    const getLastWeekProfitChartDetail = useFetchLastWeekProfitChartDetail({
        onSuccess: (response: IResponse) => {
            if (response.statusCode === 200) {
                lastWeekTradesData.current = [];
                if (response.data.lastWeekTradeChartDetail && response.data.lastWeekTradeChartDetail.length > 0) {
                    response.data.lastWeekTradeChartDetail.forEach((trade: { tradeClosedDate: any; totalProfit: any; }) => {
                        lastWeekTradesData.current.push({
                            date: formatDateTime(trade.tradeClosedDate, "DD-MMS-YYYY"),
                            value: Math.ceil(trade.totalProfit),
                        });
                    });
                }
                if (response.data.lastWeekTradeDetail) {
                    lastWeekTradeClosedTotalPrice.current = formatNumberWithCommas(response.data.lastWeekTradeDetail.tradeClosedTotalPrice);
                    lastWeekTotalTrades.current = response.data.lastWeekTradeDetail.totalTrades;
                    lastWeekTotalProfit.current = formatNumberWithCommas(response.data.lastWeekTradeDetail.totalProfit);
                    lastWeekTotalLoss.current = formatNumberWithCommas(response.data.lastWeekTradeDetail.totalLoss);
                }
            } else {
                lastWeekTradesData.current = [];
                lastWeekTradeClosedTotalPrice.current = "0.00";
                lastWeekTotalTrades.current = 0;
                lastWeekTotalProfit.current = "0.00";
                lastWeekTotalLoss.current = "0.00";
            }
        },
    });

    const LastWeekTradeDetailChart = () => {
        const lastWeekTradesConfig = {
            data: lastWeekTradesData.current,
            xField: "date",
            yField: "value",
            color: ["#FFFFFF"],
            columnWidthRatio: 0.1,
            autoFit: true,
            xAxis: {
                label: {
                    style: {
                        fill: "#FFFFFF",
                        fontSize: 12,
                    },
                },
            },
            yAxis: {
                label: {
                    style: {
                        fill: "#FFFFFF",
                    },
                },
                grid: {
                    line: {
                        style: {
                            stroke: "rgba(255, 255, 255)",
                            lineDash: [4, 4],
                        },
                    },
                },
            }
            // tooltip: {
            //     customContent: (title: any, items: any) => {
            //         <div
            //             style={{
            //                 padding: "8px", z
            //                 color: "#fff",
            //                 backgroundColor: "rgba(0, 0, 0, 0.7)",
            //                 borderRadius: "4px",
            //             }}
            //         >
            //             <div>{title}</div>
            //             {
            //                 items?.map((item: any, index: any) => (
            //                     <div key={index} style={{ marginTop: 4 }}>
            //                         <span>{item.name}: </span>
            //                         <span style={{ fontWeight: "bold" }}>{item.value}</span>
            //                     </div>
            //                 ))
            //             }
            //         </div>
            //     }
            // },
        };

        return <Column {...lastWeekTradesConfig} />;
    };

    const getLastWeekOverviewChartDetail = useFetchLastWeekOverviewChartDetail({
        onSuccess: (response: IResponseModel<LastWeekOverviewChartData>) => {
            if (response.statusCode === 200) {
                lastWeekOverviewChartData.current = response.data;
            } else {
                lastWeekOverviewChartData.current = [];
            }
        },
    });

    const TradeOverviewChart = () => {
        const tradeOverviewData: any = [];
        lastWeekOverviewChartData.current.forEach(trade => {
            tradeOverviewData.push({ date: formatDateTime(new Date(trade.tradeClosedDate), "DD-MMS-YYYY"), profit: trade.totalProfit, loss: trade.totalLoss });
        });

        const tradeOverviewConfig = {
            data: tradeOverviewData.flatMap((item: any) => [
                { date: item.date, value: item.profit, category: "Profit" },
                { date: item.date, value: item.loss, category: "Stop Loss" },
            ]),
            xField: "date",
            yField: "value",
            seriesField: "category",
            colorField: "category",
            scale: {
                color: {
                    range: ['#52C41A', '#FF4D4F'],
                },
            },
            xAxis: {
                label: {
                    style: {
                        fill: "#333",
                        fontSize: 12,
                    },
                },
            },
            yAxis: {
                label: {
                    style: {
                        fill: "#333",
                        fontSize: 12,
                    },
                },
            },
        };

        return <Line {...tradeOverviewConfig} />;
    };

    const fetchTopProfitableData = (value: Align) => {
        profitableFilterValue.current = value;
        const profitFilterRangeValue = profitableFilterValue.current === 'Last 7 day' ? 'last7d' : profitableFilterValue.current === 'Last 1 month' ? 'last1m' : profitableFilterValue.current.toLowerCase();
        getTopProfitableTrades.mutate({ userAccountId: "", filterRangeValue: profitFilterRangeValue });
    };

    const getTopProfitableTrades = useFetchTopProfitableTrades({
        onSuccess: (response: IResponseModel<TopTrades>) => {
            if (response.statusCode === 200) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                topProfitableTrades.current = formattedData;
            } else {
                topProfitableTrades.current = [];
            }
        }
    });

    const fetchTopStopLossData = (value: Align) => {
        stopLossFilterValue.current = value;
        const lossFilterRangeValue = stopLossFilterValue.current === 'Last 7 day' ? 'last7d' : stopLossFilterValue.current === 'Last 1 month' ? 'last1m' : stopLossFilterValue.current.toLowerCase();
        getTopStopLossTrades.mutate({ userAccountId: "", filterRangeValue: lossFilterRangeValue });
    };

    const getTopStopLossTrades = useFetchTopStopLossTrades({
        onSuccess: (response: IResponseModel<TopTrades>) => {
            if (response.statusCode === 200) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                topStopLossTrades.current = formattedData;
            } else {
                topStopLossTrades.current = [];
            }
        }
    });

    const fetchTopProfitableStockSymbolData = () => {
        getTopProfitableStockSymbol.mutate({ userAccountId: "", startDate: dateRange[0], endDate: dateRange[1] });
    };

    const getTopProfitableStockSymbol = useFetchTopProfitableStockSymbol({
        onSuccess: (response: IResponseModel<TopTrades>) => {
            if (response.statusCode === 200) {
                const formattedData = response.data.map((item: any, index: { toString: () => any; }) => ({
                    ...item,
                    key: index.toString(),
                }));
                topProfitableStock.current = formattedData;
            } else {
                topProfitableStock.current = [];
            }
        }
    });

    const handleDateChange = (_: any, dateStrings: any) => {
        setDateRange(dateStrings);
        getTopProfitableStockSymbol.mutate({ userAccountId: userAccountId, startDate: dateStrings[0], endDate: dateStrings[1] });
    }


    return (
        <div className="p-2.5 lg:p-6">
            <div className='flex justify-between items-center mb-5'>
                <div>
                    <div>
                        <h2 className='text-[21px] font-bold text-black text-start'>Dashboard</h2>
                    </div>
                </div>
                <div className='flex gap-3'>
                    <div>
                        <Space direction="vertical" size={12} className='w-full my-2 lg:my-0'>
                            <RangePicker
                                onChange={handleDateChange}
                                className='p-2 w-full datepicker-table'
                            />
                        </Space>
                    </div>
                    <div>
                        <Select
                            value={userAccountId}
                            className="text-start h-[34px] sm:h-10 user-dropdown min-w-60"
                            onChange={handleUserAccountChange}
                            allowClear
                            showSearch
                            options={userAccount}
                            placeholder="Select User Account"
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </div>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-2 sm:gap-4">
                <div className='bg-white p-4 lg:p-4 flex justify-between items-center rounded-xl shadow-box'>
                    <div>
                        <p className='text-start text-sm text-grey-suva font-semibold'>Total Profit</p>
                        <p className='text-start text-xl lg:text-3xl text-black opacity-85 font-bold'>${totalProfit.current} <span className='text-black text-sm font-semibold'>+0%</span></p>
                    </div>
                    <div className='bg-blue-midnight w-9 h-9 lg:w-11 lg:h-11 rounded-lg flex justify-center items-center'>
                        <img className='w-7 lg:w-8' src={Images.dollerwhite} alt="" />
                    </div>
                </div>
                <div className='bg-white p-4 lg:p-4 flex justify-between items-center rounded-xl shadow-box'>
                    <div>
                        <p className='text-start text-sm text-grey-suva font-semibold'>Today's Profit</p>
                        <p className='text-start text-xl lg:text-3xl text-black opacity-85 font-bold'>${todayProfit.current} <span className='text-black text-sm font-semibold'>+0%</span></p>
                    </div>
                    <div className='bg-blue-midnight w-9 h-9 lg:w-11 lg:h-11 rounded-lg flex justify-center items-center'>
                        <img className='w-6 lg:w-7' src={Images.toddayprofit} alt="" />
                    </div>
                </div>
                <div className='bg-white p-4 lg:p-4 flex justify-between items-center rounded-xl shadow-box'>
                    <div>
                        <p className='text-start text-sm text-grey-suva font-semibold'>Today's Loss</p>
                        <p className='text-start text-xl lg:text-3xl text-black opacity-85 font-bold'>${todayLoss.current} <span className='text-black text-sm font-semibold'>-0%</span></p>
                    </div>
                    <div className='bg-blue-midnight w-9 h-9 lg:w-11 lg:h-11 rounded-lg flex justify-center items-center'>
                        <img className='w-6 lg:w-7' src={Images.totalloss} alt="" />
                    </div>
                </div>
                <div className='bg-white p-4 lg:p-4 flex justify-between items-center rounded-xl shadow-box'>
                    <div>
                        <p className='text-start text-sm text-grey-suva font-semibold'>Total Loss</p>
                        <p className='text-start text-xl lg:text-3xl text-black opacity-85 font-bold'>${totalLoss.current} <span className='text-black text-sm font-semibold'>-0%</span></p>
                    </div>
                    <div className='bg-blue-midnight w-9 h-9 lg:w-11 lg:h-11 rounded-lg flex justify-center items-center'>
                        <img className='w-6 lg:w-7' src={Images.todayloss} alt="" />
                    </div>
                </div>
                <div className='bg-white p-4 lg:p-4 flex justify-between items-center rounded-xl shadow-box'>
                    <div>
                        <p className='text-start text-sm text-grey-suva font-semibold'>Active Trades</p>
                        <p className='text-start text-xl lg:text-3xl text-black opacity-85 font-bold'>{activeTrades.current} <span className='text-black text-sm font-semibold'>+0%</span></p>
                    </div>
                    <div className='bg-blue-midnight w-9 h-9 lg:w-11 lg:h-11 rounded-lg flex justify-center items-center'>
                        <img className='w-7 lg:w-8' src={Images.dollerwhite} alt="" />
                    </div>
                </div>
            </div>
            <div className="grid xl:grid-cols-2 gap-4 mt-6">
                <div className="p-2 lg:p-4 bg-white rounded-xl shadow-box">
                    <div className="h-96 sm:h-96 lg:h-120 xl:h-128">
                        <LastWeekTradeDetailChart />
                        {/* <div
                            id="container2"
                            ref={chartContainerRef2}
                            className="w-full chart-container bg-white">
                        </div> */}
                    </div>
                    <div className="text-start mt-2 sm:mt-4">
                        <p className="font-bold text-base text-black opacity-85">Last 7 Days Overview</p>
                        <p className="text-sm text-grey-suva font-semibold">Than last week <span className="text-green-dark-pastel font-bold">30%</span></p>
                        <p className="text-xs sm:text-sm text-grey-suva font-semibold mt-2 sm:mt-4">We have created multiple options for you to put together and customise into pixel perfect pages.</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 text-start mt-4 gap-2">
                        <div>
                            <p className="font-bold text-xl text-black opacity-85">${lastWeekTradeClosedTotalPrice.current}</p>
                            <p className="text-sm text-grey-suva font-semibold">Valued Trade</p>
                        </div>
                        <div>
                            <p className="font-bold text-xl text-black opacity-85">{lastWeekTotalTrades.current}</p>
                            <p className="text-sm text-grey-suva font-semibold">Trades</p>
                        </div>
                        <div>
                            <p className="font-bold text-xl text-black opacity-85">${lastWeekTotalProfit.current}</p>
                            <p className="text-sm text-grey-suva font-semibold">Profit Booked</p>
                        </div>
                        <div>
                            <p className="font-bold text-xl text-black opacity-85">${lastWeekTotalLoss.current}</p>
                            <p className="text-sm text-grey-suva font-semibold">Stoploss Triggered</p>
                        </div>
                    </div>
                </div>
                <div className="p-4 bg-white rounded-xl shadow-box">
                    <h4 className="font-bold text-base text-black opacity-85 text-start">Trades Overview</h4>
                    <p className="text-sm text-grey-suva font-semibold text-start mb-4">Profitable Trades Changed in last 7 days <span className="text-green-dark-pastel font-bold">30%</span></p>
                    <div className="lg:h-120 xl:h-128">
                        <TradeOverviewChart />
                        {/* <div
                            id="container1"
                            ref={chartContainerRef1}
                            className="w-full chart-container bg-white"
                            style={{ width: '100%', height: '100%' }} // Ensure the container takes up 100% width and height
                        >
                        </div> */}
                    </div>
                </div>
            </div>
            <div className='grid md:grid-cols-2 gap-4 mt-6'>
                <div className='bg-white p-2 lg:p-4 rounded-xl shadow-box'>
                    <div className='block lg:flex justify-between items-center'>
                        <div>
                            <p className='text-base text-start font-semibold text-black opacity-85'>Top Profitable Trades</p>
                        </div>
                        <div>
                            <Segmented
                                value={profitableFilterValue.current}
                                className='mb-2 mt-2 lg:mt-0'
                                onChange={fetchTopProfitableData}
                                options={['Today', 'Yesterday', 'Last 7 day', 'Last 1 month']}
                            />
                        </div>
                    </div>
                    {
                        topProfitableTrades.current.map((item, index) => (
                            <div className='flex justify-between items-center mt-5' key={index + 1}>
                                <div className='flex items-center'>
                                    <div>
                                        {item.realizedEarning > 0 ? (
                                            <p className='text-green-dark-pastel bg-green-granny rounded-full w-6 h-6 text-[15px]'>+</p>
                                        ) : (
                                            <p className='text-red-persian bg-red-300 rounded-full w-6 h-6 text-[15px]'>-</p>
                                        )}
                                    </div>
                                    <div className='ml-2 sm:ml-4'>
                                        <p className='text-[13px] sm:text-sm font-semibold text-black opacity-85 text-start'>{item.contractName}</p>
                                        <p className='font-semibold text-[11px] sm:text-xs text-grey-suva text-start'>{formatDateTime(new Date(item.closedAt), "DD MML YYYY, HH:mm AMPM")}</p>
                                    </div>
                                </div>
                                <div>
                                    {item.realizedEarning > 0 ? (
                                        <p className='text-sm sm:text-base font-bold text-green-dark-pastel'>+ ${formatNumberWithCommas(item.realizedEarning)}</p>
                                    ) : (
                                        <p className='text-sm sm:text-base font-bold text-red-persian'>- ${formatNumberWithCommas(item.realizedEarning)}</p>
                                    )}

                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className='bg-white p-2 lg:p-4 rounded-xl shadow-box'>
                    <div className='block lg:flex justify-between items-center'>
                        <div>
                            <p className='text-base text-start font-semibold text-black opacity-85'>Top Stoploss Trades</p>
                        </div>
                        <div>
                            <Segmented
                                value={stopLossFilterValue.current}
                                className='mb-2 mt-2 lg:mt-0'
                                onChange={fetchTopStopLossData}
                                options={['Today', 'Yesterday', 'Last 7 day', 'Last 1 month']}
                            />
                        </div>
                    </div>
                    {
                        topStopLossTrades.current.map((item, index) => (
                            <div className='flex justify-between items-center mt-5' key={index + 1}>
                                <div className='flex items-center'>
                                    <div>
                                        {item.realizedEarning > 0 ? (
                                            <p className='text-green-dark-pastel bg-green-granny rounded-full w-6 h-6 text-[15px]'>+</p>
                                        ) : (
                                            <p className='text-red-persian bg-red-300 rounded-full w-6 h-6 text-[15px]'>-</p>
                                        )}
                                    </div>
                                    <div className='ml-2 sm:ml-4'>
                                        <p className='text-[13px] sm:text-sm font-semibold text-black opacity-85 text-start'>{item.contractName}</p>
                                        <p className='font-semibold text-[11px] sm:text-xs text-grey-suva text-start'>{formatDateTime(new Date(item.closedAt), "DD MML YYYY, HH:mm AMPM")}</p>
                                    </div>
                                </div>
                                <div>
                                    {item.realizedEarning > 0 ? (
                                        <p className='text-sm sm:text-base font-bold text-green-dark-pastel'>+ ${formatNumberWithCommas(item.realizedEarning)}</p>
                                    ) : (
                                        <p className='text-sm sm:text-base font-bold text-red-persian'>- ${formatNumberWithCommas(item.realizedEarning)}</p>
                                    )}

                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className='bg-white p-2 lg:p-4 rounded-xl shadow-box'>
                    <div className='block lg:flex justify-between items-center'>
                        <div>
                            <p className='text-base text-start font-semibold text-black opacity-85'>Top Profitable Stock</p>
                        </div>
                    </div>
                    {
                        topProfitableStock.current.map((item, index) => (
                            <div className='flex justify-between items-center mt-5' key={index + 1}>
                                <div className='flex items-center'>
                                    <div>
                                        {item.totalRealizedEarning > 0 ? (
                                            <p className='text-green-dark-pastel bg-green-granny rounded-full w-6 h-6 text-[15px]'>+</p>
                                        ) : (
                                            <p className='text-red-persian bg-red-300 rounded-full w-6 h-6 text-[15px]'>-</p>
                                        )}
                                    </div>
                                    <div className='ml-2 sm:ml-4'>
                                        <p className='text-[13px] sm:text-sm font-semibold text-black opacity-85 text-start'>{item.parentStockSymbol}</p>
                                    </div>
                                </div>
                                <div>
                                    {item.totalRealizedEarning > 0 ? (
                                        <p className='text-sm sm:text-base font-bold text-green-dark-pastel'>+ ${formatNumberWithCommas(item.totalRealizedEarning)}</p>
                                    ) : (
                                        <p className='text-sm sm:text-base font-bold text-red-persian'>- ${formatNumberWithCommas(item.totalRealizedEarning)}</p>
                                    )}

                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>
        </div>
    );
};

export default Dashboard;